import { Divider, Typography } from "@mui/material";
import PortalSnackbar, { PortalSnackbarProps } from "../common/PortalSnackbar";
import { createHierarchyNode, deleteHierarchyNode, updateHierarchyNode } from "domains/nodes/services/node-service";
import { useEffect, useState } from "react";

import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import EditNodeButtons from "./components/action-buttons/EditNodeButtons";
import EditableNodeDetails from "./components/editable-form/EditableNodeDetails";
import { NodeDetailsDto } from "../common/NodeDetailsDto";
import PortalConfirmationDialog from "../common/PortalConfirmationDialog";
import ReadOnlyNodeDetails from "./components/readonly-form/ReadOnlyNodeDetails";
import { ServerValidationErrors } from "components/ServerValidationErrors";
import localisations from "../../localisation";

interface IActionStatus {
    isSavingChanges?: boolean;
    isDeleting?: boolean;
    isSavingChild?: boolean;
}

interface IProps {
    fieldCompanyId: number;
    nodeDetails?: NodeDetailsDto;
    onEditClicked?: () => void;
    onCancelEditClicked?: () => void;
    onSaveComplete?: () => void;
    onDeleteComplete?: () => void;
}

interface ServerError {
    showError?: boolean;
    errorMessage?: JSX.Element | null;
}

const NodeDetails = (props: IProps) => {
    const { pleaseSelecteDetailsText, headerText, addChildNodeForm, deleteDialog, components, buttons, messages } =
        localisations.nodeDetails;

    const { nodeDetails, fieldCompanyId, onEditClicked, onCancelEditClicked, onSaveComplete, onDeleteComplete } = props;

    const [internalNodeDetails, setInternalNodeDetails] = useState<NodeDetailsDto | undefined>(nodeDetails);

    const [isInReadOnlyMode, setIsInReadOnlyMode] = useState(true);

    const [showDeleteDialog, setShowDeleteDialog] = useState(false);

    const [showAddChild, setShowAddChild] = useState(false);

    const [isDeleted, setIsDeleted] = useState(false);

    const [showErrors, setShowErrors] = useState<ServerError>();

    const [actionStatus, setActionStatus] = useState<IActionStatus>({
        isDeleting: false,
        isSavingChanges: false,
        isSavingChild: false,
    });

    const [snackbar, setSnackbar] = useState<PortalSnackbarProps>({
        message: "",
        show: false,
    });

    const handleDeleteNodeClick = () => {
        resetErrors();
        setShowDeleteDialog(true);
    };

    const handleCancelDeleteNode = () => {
        resetErrors();
        setShowDeleteDialog(false);
    };

    const handleDeleteConfirmationClick = async () => {
        if (nodeDetails) {
            try {
                setActionStatus({ isDeleting: true });
                await deleteHierarchyNode(nodeDetails.id);
                setIsDeleted(true);
                setSnackbar({
                    message: messages.deletedNodeSuccess,
                    severity: "success",
                    show: true,
                });
                onDeleteComplete?.();
            } catch (error: any) {
                handelServerErrors(messages.deletedNodeError, error);
            } finally {
                setActionStatus({ isDeleting: false });
            }
        }
        setShowDeleteDialog(false);
    };

    const handleAddChildNodeClick = () => {
        resetErrors();
        setShowAddChild(true);
    };

    const handleSaveChildClick = async () => {
        resetErrors();
        if (internalNodeDetails) {
            try {
                setActionStatus({ isSavingChild: true });
                await createHierarchyNode({
                    fieldCompanyId: fieldCompanyId,
                    allowPartitionChildren: internalNodeDetails.allowPartitions,
                    name: internalNodeDetails.name,
                    reference: internalNodeDetails.reference ? internalNodeDetails.reference : null,
                    parentId: internalNodeDetails.id,
                });

                setSnackbar({
                    message: messages.createdChildeSuccess,
                    severity: "success",
                    show: true,
                });

                onSaveComplete?.();
            } catch (error: any) {
                handelServerErrors(messages.createdChildeError, error);
            } finally {
                setActionStatus({ isSavingChild: false });
            }
        }
    };

    const handleEditDetailsClick = () => {
        resetErrors();
        setIsInReadOnlyMode(false);
        onEditClicked?.();
    };

    const handleSaveChangesClick = async () => {
        resetErrors();
        if (internalNodeDetails) {
            try {
                setActionStatus({ isSavingChanges: true });
                await updateHierarchyNode(internalNodeDetails.id, {
                    allowPartitionChildren: internalNodeDetails.allowPartitions,
                    name: internalNodeDetails.name,
                    reference: internalNodeDetails.reference ? internalNodeDetails.reference : null,
                });

                setSnackbar({
                    message: messages.updatedNodeSuccess,
                    severity: "success",
                    show: true,
                });

                onSaveComplete?.();

                setShowAddChild(false);
            } catch (error: any) {
                handelServerErrors(messages.updatedNodeError, error);
            } finally {
                setActionStatus({ isSavingChanges: false });
            }
        }
    };

    const handleCancelChangesClick = () => {
        resetErrors();
        resetNodeDetails();
        onCancelEditClicked?.();
    };

    const handelServerErrors = (title: string, error: any) => {
        setShowErrors({
            showError: true,
            errorMessage: ServerValidationErrors({
                error: error,
                localisations: localisations.nodeDetails.serverErrors,
                title: title,
            }),
        });
    };

    const handleDetailsEdited = (newNodeDetails: NodeDetailsDto) => {
        setInternalNodeDetails(newNodeDetails);
    };

    const resetNodeDetails = () => {
        setIsInReadOnlyMode(true);
        setShowAddChild(false);
        setIsDeleted(false);
        resetErrors();
        if (nodeDetails) {
            setInternalNodeDetails(nodeDetails);
        }
    };

    const handlePortalSnackbarClose = () => {
        setSnackbar({
            show: false,
        });
    };

    const resetErrors = () => {
        setShowErrors({
            showError: false,
            errorMessage: null,
        });
    };

    useEffect(() => {
        resetNodeDetails();
    }, [nodeDetails]);

    return (
        <>
            <Typography sx={{ mb: 1 }} variant="h6">
                {headerText}
            </Typography>
            {(!nodeDetails || isDeleted) && <Typography>{pleaseSelecteDetailsText}</Typography>}
            {nodeDetails && !isDeleted && (
                <>
                    {isInReadOnlyMode && <ReadOnlyNodeDetails nodeDetails={nodeDetails} />}
                    {!isInReadOnlyMode && (
                        <EditableNodeDetails
                            aria-label={components.editableNodeDetails}
                            nodeDetails={nodeDetails}
                            onEdit={handleDetailsEdited}
                        />
                    )}

                    <Divider sx={{ mb: 2, mt: 2 }} />

                    {showAddChild && (
                        <>
                            <Typography sx={{ mb: 1 }} variant="subtitle1">
                                {`${addChildNodeForm.headerText} "${nodeDetails.name}"`}
                            </Typography>
                            <EditableNodeDetails
                                aria-label={components.createChildNode}
                                nodeDetails={{
                                    allowPartitions: false,
                                    parentId: nodeDetails.parentId,
                                    id: nodeDetails.id,
                                    hasChildren: false,
                                    name: "",
                                    reference: "",
                                }}
                                onEdit={handleDetailsEdited}
                            />
                            <Divider sx={{ mb: 2, mt: 2 }} />
                        </>
                    )}

                    <EditNodeButtons
                        isSavingChanges={actionStatus.isSavingChanges}
                        isDeleting={actionStatus.isDeleting}
                        isSavingChild={actionStatus.isSavingChild}
                        addChildMode={showAddChild}
                        allowDelete={!nodeDetails.hasChildren}
                        enterEditMode={!isInReadOnlyMode}
                        onEditClicked={handleEditDetailsClick}
                        onCancelChangesClicked={handleCancelChangesClick}
                        onSaveChangesClicked={handleSaveChangesClick}
                        onDeleteClicked={handleDeleteNodeClick}
                        onAddChildClicked={handleAddChildNodeClick}
                        onSaveChildClicked={handleSaveChildClick}
                    />
                    {showErrors?.showError && (
                        <>
                            <Divider sx={{ mb: 2, mt: 2 }} />
                            {showErrors.errorMessage}
                        </>
                    )}
                </>
            )}
            <PortalConfirmationDialog
                open={showDeleteDialog}
                title={deleteDialog.title}
                contentText={`${deleteDialog.contentText} "${nodeDetails?.name}"?`}
                cancelText={buttons.cancelDeleteButtonText}
                confirmationText={buttons.confirmDeleteButtonText}
                isPerformingAction={actionStatus.isDeleting}
                buttonIcon={<DeleteForeverIcon />}
                cancelColor="inherit"
                cancelVarient="outlined"
                confirmationColor="error"
                confirmationVarient="contained"
                onConfirmation={handleDeleteConfirmationClick}
                onCancel={handleCancelDeleteNode}
            />
            <PortalSnackbar
                message={snackbar.message}
                severity={snackbar.severity}
                show={snackbar.show}
                clickToClose={snackbar.clickToClose}
                autoHideDuration={10000}
                onClose={handlePortalSnackbarClose}
            ></PortalSnackbar>
        </>
    );
};

export default NodeDetails;
